"use client";

interface Props {
  /** Lifts a tab navigation request back to TodayScreen so the
   *  parent can route to the destination tab. */
  onNavigate: (tab: string) => void;
  /** Whether the user currently has at least one active goal. Drives
   *  the reminder variant: zero-goal users get nudged to Goals, users
   *  with goals get nudged to Plan. */
  hasActiveGoals: boolean;
}

/**
 * Phase 9-1.a (revised 2026-05-24) — Today-page reminder hint.
 *
 * Renders below `RestartBlock` when the Today projection has no
 * `startHereFocus`. Two variants based on whether the user has any
 * active goals at all:
 *
 *   1. Zero-goal user → nudge to Goals to set their first one.
 *      Going to Plan would be meaningless — AI has nothing to plan
 *      around.
 *   2. Has-goals-but-no-focus → nudge to Plan to build the day.
 *
 * Pure UI reminder — does NOT call `/api/plan/empty-state-trigger`
 * and does NOT render an inline preview. Today is for execution;
 * Plan / Goals own their respective surfaces.
 *
 * Earlier card-shaped version (large border + "PLAN SOMETHING"
 * header + Phase 8-4.c inline 4 AM preview) was the wrong product
 * shape — it competed with `RestartBlock` for visual weight and
 * surfaced an untrustworthy default-template preview when the user
 * had no working-hours signal.
 *
 * The 8-4.a / 8-4.c backend endpoints remain on the server (other
 * surfaces may consume them); just not from Today.
 */
export default function EmptyStatePlanDayCta({
  onNavigate,
  hasActiveGoals,
}: Props) {
  if (hasActiveGoals) {
    return (
      <div
        className="mt-3 flex items-center justify-center gap-2 px-3 py-2 text-xs text-text-light"
        data-testid="today-plan-reminder"
      >
        <span aria-hidden>🌿</span>
        <span>No plan yet for today.</span>
        <button
          type="button"
          // UAT 2026-05-25 manual Chrome — tab id is "schedule", not
          // "plan". page.tsx's onNavigateState handler maps
          // "schedule", "decide", "goals" etc. but routes "plan" into
          // the else branch as setActiveTab("plan") which is an
          // unknown tab id, so the click was a silent no-op.
          onClick={() => onNavigate("schedule")}
          data-testid="today-plan-reminder-open"
          className="font-medium text-forest-med underline-offset-2 hover:underline focus-visible:underline"
        >
          Open Plan →
        </button>
      </div>
    );
  }

  // Zero-goal variant: nudge to Goals. Different emoji (seedling) so
  // the visual cue maps to "starting fresh" rather than "today is
  // empty".
  return (
    <div
      className="mt-3 flex items-center justify-center gap-2 px-3 py-2 text-xs text-text-light"
      data-testid="today-goals-reminder"
    >
      <span aria-hidden>🌱</span>
      <span>No goals yet — let&apos;s add one.</span>
      <button
        type="button"
        onClick={() => onNavigate("goals")}
        data-testid="today-goals-reminder-open"
        className="font-medium text-forest-med underline-offset-2 hover:underline focus-visible:underline"
      >
        Open Goals →
      </button>
    </div>
  );
}
